/**
 * Plain-language category catalog (spec section 10). Data only; the rules
 * that turn a category into rows live in rules.ts and the compiler.
 *
 * Ids are stable: they are stored on `VerifikationMeta.categoryId` and in
 * merchant rules, so never rename or reuse one. Labels may change. Every
 * `accountNumber` must exist in the BAS reference (tested), every entry has
 * a momskod-carrying `defaultVat`, `deductible`, `reportLine`, `entityTypes`
 * and `keywords` (tested).
 *
 * VAT defaults: `mode: 'none'` = the purchase carries no VAT at all (bank
 * fees, insurance, rent, interest, membership fees); `domestic` with rate 0 =
 * a VAT-exempt sale reported in box 42.
 *
 * Deliberate deviations from the spec's example numbers (section 10):
 *   - BRF fastighet: the spec lists "Fastighetsskötsel 5170, Reparationer
 *     5190, Värme/el 5020/5060", which transposes the BAS names. The catalog
 *     follows BAS 2026: 5170 Reparation och underhåll av fastighet,
 *     5190 Övriga fastighetskostnader (fastighetsskötsel & drift), 5030
 *     Värme, 5040 Vatten och avlopp, while El (5020) and Städning &
 *     renhållning (5060) are the shared entries offered to every entity.
 *   - Personalfika 7690 is "hidden for EF without employees". CompanySettings
 *     has no employee flag in v1, so `catalogFor('enskild_firma')` hides it
 *     unless the caller passes `{ hasEmployees: true }`; an EF owner's own
 *     fika is private either way.
 */
import type { EntityType, VatChoice } from '../types';
import type { Category } from './types';

const ALL: EntityType[] = ['aktiebolag', 'enskild_firma', 'ideell_forening', 'bostadsrattsforening'];
const NOT_BRF: EntityType[] = ['aktiebolag', 'enskild_firma', 'ideell_forening'];
const FORENING: EntityType[] = ['ideell_forening'];
const BRF: EntityType[] = ['bostadsrattsforening'];

const v25: VatChoice = { rate: 25, mode: 'domestic', momskod: '48' };
const v12: VatChoice = { rate: 12, mode: 'domestic', momskod: '48' };
const v6: VatChoice = { rate: 6, mode: 'domestic', momskod: '48' };
/** No VAT on the purchase at all (nothing to report). */
const none: VatChoice = { rate: 0, mode: 'none' };
/** Domestic sale with VAT; the compiler picks 2610/2620/2630 and box 05. */
const sale25: VatChoice = { rate: 25, mode: 'domestic', momskod: '05' };
const sale12: VatChoice = { rate: 12, mode: 'domestic', momskod: '05' };
const sale6: VatChoice = { rate: 6, mode: 'domestic', momskod: '05' };
/** VAT-exempt sale, reported in box 42. */
const saleExempt: VatChoice = { rate: 0, mode: 'domestic', momskod: '42' };

const EXTERNA = 'rr.ovriga_externa_kostnader';

function purchase(
  id: string,
  label: string,
  accountNumber: number,
  formalName: string,
  group: string,
  defaultVat: VatChoice,
  entityTypes: EntityType[],
  hint: string,
  examples: string,
  keywords: string[],
  extra: Partial<Pick<Category, 'deductible' | 'reportLine' | 'rule'>> = {},
): Category {
  const base: Category = {
    id,
    label,
    hint,
    examples,
    formalName,
    group,
    kind: 'purchase',
    accountNumber,
    defaultVat,
    deductible: extra.deductible ?? 'full',
    entityTypes,
    keywords,
    reportLine: extra.reportLine ?? EXTERNA,
  };
  return extra.rule ? { ...base, rule: extra.rule } : base;
}

function sale(
  id: string,
  label: string,
  accountNumber: number,
  formalName: string,
  group: string,
  defaultVat: VatChoice,
  entityTypes: EntityType[],
  hint: string,
  examples: string,
  keywords: string[],
  reportLine = 'rr.nettoomsattning',
): Category {
  return {
    id,
    label,
    hint,
    examples,
    formalName,
    group,
    kind: 'sale',
    accountNumber,
    defaultVat,
    deductible: 'full',
    entityTypes,
    keywords,
    reportLine,
  };
}

export const GROUPS = {
  kontor: 'Kontor & IT',
  resor: 'Resor',
  representation: 'Representation & personal',
  lokal: 'Lokal & drift',
  tjanster: 'Köpta tjänster',
  bank: 'Bank & försäkring',
  marknadsforing: 'Marknadsföring',
  varor: 'Varor & frakt',
  ovrigt: 'Övrigt',
  forsaljning: 'Försäljning & inkomster',
  forening: 'Föreningen',
  fastighet: 'Fastigheten',
} as const;

/** The default account for BRF årsavgifter; `CompanySettings.arsavgiftAccount` may override it. */
export const BRF_ARSAVGIFT_DEFAULT_ACCOUNT = 3910;
export const BRF_ARSAVGIFT_ACCOUNTS: readonly number[] = [3910, 3020, 3021];
export const BRF_ARSAVGIFT_CATEGORY_ID = 'brf_arsavgifter';
/** Hidden for an enskild firma without employees: the owner's own fika is private. */
export const PERSONALFIKA_CATEGORY_ID = 'personalfika';

export const CATALOG: readonly Category[] = [
  // ---- Kontor & IT ----------------------------------------------------------
  purchase('kontorsmaterial', 'Kontorsmaterial', 6110, 'Kontorsmateriel', GROUPS.kontor, v25, ALL,
    'Småsaker till kontoret.', 'papper, pennor, pärmar, toner, kuvert',
    ['kontorsmaterial', 'papper', 'pennor', 'toner', 'bläck', 'pärm', 'kuvert', 'office supplies', 'staples', 'clas ohlson']),
  purchase('programvara', 'Programvara & prenumerationer', 5420, 'Programvaror', GROUPS.kontor, v25, ALL,
    'Program och digitala tjänster ni betalar för. Utländsk säljare? Då gäller omvänd moms.', 'Microsoft 365, Adobe, Fortnox, Dropbox, appar',
    ['programvara', 'prenumeration', 'licens', 'saas', 'software', 'subscription', 'microsoft', 'adobe', 'google workspace', 'dropbox', 'notion', 'slack', 'zoom', 'app store'],
    { rule: 'foreign_saas' }),
  purchase('it_tjanster', 'IT-tjänster & webbhotell', 6540, 'IT-tjänster', GROUPS.kontor, v25, ALL,
    'Drift, webbhotell, domäner och IT-support.', 'webbhotell, domän, molnlagring, IT-support',
    ['it-tjänster', 'webbhotell', 'hosting', 'domän', 'server', 'moln', 'cloud', 'support', 'loopia', 'one.com', 'binero', 'aws', 'hetzner']),
  purchase('telefon', 'Telefon', 6212, 'Mobiltelefon', GROUPS.kontor, v25, ALL,
    'Telefonabonnemang och samtal.', 'mobilabonnemang, kontantkort, växel',
    ['telefon', 'mobil', 'abonnemang', 'telia', 'tele2', 'telenor', 'tre', 'hallon', 'comviq', 'phone']),
  purchase('internet', 'Internet & bredband', 6230, 'Datakommunikation', GROUPS.kontor, v25, ALL,
    'Bredband och uppkoppling.', 'fiber, bredband, mobilt bredband',
    ['internet', 'bredband', 'fiber', 'bahnhof', 'comhem', 'tele2', 'telia', 'ownit', 'broadband', 'router']),
  purchase('datorer_teknik', 'Datorer & teknik', 5410, 'Förbrukningsinventarier', GROUPS.kontor, v25, ALL,
    'Datorer och annan teknisk utrustning.', 'dator, skärm, mobiltelefon, tangentbord, skrivare',
    ['dator', 'laptop', 'skärm', 'monitor', 'mobiltelefon', 'iphone', 'ipad', 'tangentbord', 'mus', 'skrivare', 'headset', 'elgiganten', 'webhallen', 'dustin', 'kjell', 'mediamarkt', 'apple', 'inventarier', 'förbrukningsinventarier'],
    { rule: 'asset_threshold' }),
  purchase('mobler_inredning', 'Möbler & inredning', 5410, 'Förbrukningsinventarier', GROUPS.kontor, v25, ALL,
    'Möbler, belysning och inredning.', 'skrivbord, kontorsstol, hylla, lampa, matta',
    ['möbel', 'möbler', 'inredning', 'skrivbord', 'stol', 'kontorsstol', 'hylla', 'bokhylla', 'lampa', 'belysning', 'matta', 'soffa', 'bord', 'ikea', 'kinnarps', 'jysk', 'mio', 'furniture', 'desk', 'chair', 'inventarier'],
    { rule: 'asset_threshold' }),

  // ---- Resor ----------------------------------------------------------------
  purchase('resor', 'Resor', 5800, 'Resekostnader', GROUPS.resor, v6, ALL,
    'Tåg, flyg och buss i tjänsten.', 'SJ, SAS, Flixbus, Vy',
    ['resa', 'tåg', 'flyg', 'buss', 'sj', 'sas', 'norwegian', 'flixbus', 'vy', 'mtr', 'snälltåget', 'travel', 'train', 'flight']),
  purchase('hotell', 'Hotell & logi', 5831, 'Kost och logi i Sverige', GROUPS.resor, v12, ALL,
    'Övernattning på tjänsteresa.', 'hotell, vandrarhem, Airbnb',
    ['hotell', 'hotel', 'logi', 'övernattning', 'scandic', 'nordic choice', 'elite', 'airbnb', 'booking.com', 'hostel']),
  purchase('lokala_resor', 'Parkering, taxi & lokalresor', 5810, 'Biljetter', GROUPS.resor, v6, ALL,
    'Kortare resor och parkering i tjänsten. Parkering har 25 % moms.', 'SL, taxi, Uber, parkering, Västtrafik',
    ['parkering', 'taxi', 'uber', 'bolt', 'sl', 'västtrafik', 'skånetrafiken', 'tunnelbana', 'pendeltåg', 'easypark', 'aimo', 'parkster', 'lokaltrafik']),

  // ---- Representation & personal --------------------------------------------
  purchase('representation', 'Representation', 6072, 'Representation, ej avdragsgill', GROUPS.representation, v12, ALL,
    'Mat och dryck med kunder eller samarbetspartner. Momsen dras av på högst 300 kr per person.', 'lunch med kund, middag efter möte',
    ['representation', 'lunch', 'middag', 'restaurang', 'kund', 'kundmöte', 'fika med kund', 'afterwork', 'krog', 'café'],
    { deductible: 'partial', rule: 'representation' }),
  purchase(PERSONALFIKA_CATEGORY_ID, 'Personalfika & personalvård', 7690, 'Övriga personalkostnader', GROUPS.representation, v12, ALL,
    'Fika, frukt och enklare förtäring till personal eller ideella.', 'kaffe, frukt, fikabröd, julbord',
    ['fika', 'kaffe', 'frukt', 'personal', 'personalfika', 'fikabröd', 'kexchoklad', 'julbord', 'personalvård', 'friskvård'],
    { reportLine: 'rr.personalkostnader' }),

  // ---- Lokal & drift --------------------------------------------------------
  purchase('lokalhyra', 'Lokalhyra', 5010, 'Lokalhyra', GROUPS.lokal, none, ALL,
    'Hyra för kontor eller lokal. Oftast utan moms.', 'kontorshyra, kontorshotell, lagerhyra',
    ['hyra', 'lokalhyra', 'kontorshotell', 'coworking', 'lokal', 'kontor', 'rent', 'office']),
  purchase('el', 'El', 5020, 'El', GROUPS.lokal, v25, ALL,
    'Elräkningen.', 'Vattenfall, Fortum, Ellevio, elnät',
    ['el', 'elräkning', 'elnät', 'vattenfall', 'fortum', 'ellevio', 'eon', 'tibber', 'göteborg energi', 'electricity']),
  purchase('stadning_renhallning', 'Städning & renhållning', 5060, 'Städning och renhållning', GROUPS.lokal, v25, ALL,
    'Städning, sophämtning och avfall.', 'städfirma, sophämtning, återvinning',
    ['städning', 'städ', 'sophämtning', 'sopor', 'avfall', 'renhållning', 'återvinning', 'cleaning']),

  // ---- Köpta tjänster -------------------------------------------------------
  purchase('redovisning', 'Redovisning & bokföring', 6530, 'Redovisningstjänster', GROUPS.tjanster, v25, ALL,
    'Redovisningskonsult, bokslut och deklaration.', 'redovisningsbyrå, bokslut, deklarationshjälp',
    ['redovisning', 'bokföring', 'bokslut', 'deklaration', 'revisor', 'redovisningsbyrå', 'accounting']),
  purchase('konsult', 'Konsulter & externa tjänster', 6550, 'Konsultarvoden', GROUPS.tjanster, v25, ALL,
    'Inhyrd hjälp och rådgivning.', 'jurist, designer, utvecklare, rådgivare',
    ['konsult', 'konsultarvode', 'jurist', 'advokat', 'designer', 'utvecklare', 'frilans', 'rådgivning', 'consulting']),

  // ---- Bank & försäkring ----------------------------------------------------
  purchase('bankavgifter', 'Bankavgifter', 6570, 'Bankkostnader', GROUPS.bank, none, ALL,
    'Avgifter från banken och betalväxlar. Ingen moms.', 'kontoavgift, kortavgift, Swish-avgift, Stripe-avgift',
    ['bankavgift', 'kontoavgift', 'kortavgift', 'swish', 'stripe', 'klarna', 'izettle', 'zettle', 'bankkostnad', 'bank fee']),
  purchase('forsakring', 'Försäkring', 6310, 'Företagsförsäkringar', GROUPS.bank, none, ALL,
    'Företagets försäkringar. Ingen moms.', 'företagsförsäkring, ansvarsförsäkring, fastighetsförsäkring',
    ['försäkring', 'företagsförsäkring', 'ansvarsförsäkring', 'if', 'trygg-hansa', 'länsförsäkringar', 'folksam', 'gjensidige', 'insurance']),

  // ---- Marknadsföring -------------------------------------------------------
  purchase('marknadsforing', 'Marknadsföring & annonser', 5910, 'Annonsering', GROUPS.marknadsforing, v25, ALL,
    'Annonser och reklam. Google och Meta är utländska säljare med omvänd moms.', 'Google Ads, Meta, annons, mässa, trycksaker',
    ['marknadsföring', 'annons', 'reklam', 'google ads', 'meta', 'facebook', 'instagram', 'linkedin', 'mässa', 'trycksak', 'advertising', 'marketing']),

  // ---- Varor & frakt --------------------------------------------------------
  purchase('inkop_varor', 'Inköp av varor för försäljning', 4010, 'Inköp av handelsvaror i Sverige', GROUPS.varor, v25, NOT_BRF,
    'Varor och material som säljs vidare eller ingår i det ni säljer.', 'handelsvaror, material, råvaror',
    ['varor', 'inköp', 'handelsvaror', 'material', 'råvaror', 'lager', 'grossist', 'goods', 'wholesale'],
    { reportLine: 'rr.ravaror_handelsvaror' }),
  purchase('frakt', 'Frakt & transport', 5710, 'Frakter och försäkringar vid varudistribution', GROUPS.varor, v25, ALL,
    'Frakt av varor och paket.', 'PostNord paket, DHL, Schenker, bud',
    ['frakt', 'transport', 'paket', 'dhl', 'schenker', 'ups', 'fedex', 'bring', 'budbil', 'shipping', 'freight']),
  purchase('porto', 'Porto', 6250, 'Porto', GROUPS.varor, v25, ALL,
    'Brev och frimärken.', 'frimärken, brev, rekommenderat',
    ['porto', 'frimärke', 'brev', 'postnord', 'rek', 'postage']),

  // ---- Övrigt ---------------------------------------------------------------
  purchase('ovrig_kostnad', 'Övrig kostnad', 6991, 'Övriga externa kostnader, avdragsgilla', GROUPS.ovrigt, v25, ALL,
    'När inget annat passar. Skriv gärna vad det gällde.', 'medlemskap, avgifter, småköp',
    ['övrigt', 'övrig kostnad', 'annat', 'diverse', 'avgift', 'other']),
  purchase('ranta_lan', 'Ränta på lån', 8410, 'Räntekostnader för långfristiga skulder', GROUPS.ovrigt, none, ALL,
    'Ränta på företagets eller föreningens lån. Ingen moms.', 'banklån, fastighetslån',
    ['ränta', 'räntekostnad', 'lån', 'amortering', 'banklån', 'bolån', 'interest'],
    { reportLine: 'rr.rantekostnader' }),

  // ---- Föreningen (utgifter) ------------------------------------------------
  purchase('lamnade_bidrag', 'Lämnade bidrag', 6710, 'Lämnade bidrag', GROUPS.forening, none, FORENING,
    'Bidrag och gåvor föreningen ger vidare.', 'stipendier, bidrag till andra föreningar, insamlingar',
    ['bidrag', 'gåva', 'stipendium', 'donation', 'insamling', 'lämnat bidrag'],
    { reportLine: 'rr.lamnade_bidrag' }),

  // ---- Fastigheten (BRF) ----------------------------------------------------
  purchase('brf_reparationer', 'Reparationer & underhåll', 5170, 'Reparation och underhåll av fastighet', GROUPS.fastighet, v25, BRF,
    'Reparationer och underhåll av huset.', 'rörmokare, elektriker, målning, stambyte',
    ['reparation', 'underhåll', 'rörmokare', 'vvs', 'elektriker', 'målning', 'hantverkare', 'stambyte', 'fasad', 'tak']),
  purchase('brf_fastighetsskotsel', 'Fastighetsskötsel & drift', 5190, 'Övriga fastighetskostnader', GROUPS.fastighet, v25, BRF,
    'Löpande skötsel av fastigheten.', 'trappstädning, snöröjning, trädgård, hissservice',
    ['fastighetsskötsel', 'skötsel', 'snöröjning', 'trädgård', 'hiss', 'trappstädning', 'förvaltning', 'teknisk förvaltning']),
  purchase('brf_fastighetsavgift', 'Fastighetsavgift & fastighetsskatt', 5191, 'Fastighetsskatt/fastighetsavgift', GROUPS.fastighet, none, BRF,
    'Kommunal fastighetsavgift och fastighetsskatt. Ingen moms.', 'fastighetsavgift, fastighetsskatt',
    ['fastighetsavgift', 'fastighetsskatt', 'skatteverket fastighet']),
  purchase('brf_varme', 'Värme', 5030, 'Värme', GROUPS.fastighet, v25, BRF,
    'Fjärrvärme och uppvärmning.', 'fjärrvärme, pellets, olja',
    ['värme', 'fjärrvärme', 'uppvärmning', 'stockholm exergi', 'pellets', 'heating']),
  purchase('brf_vatten', 'Vatten & avlopp', 5040, 'Vatten och avlopp', GROUPS.fastighet, v25, BRF,
    'VA-räkningen.', 'vatten, avlopp, VA-avgift',
    ['vatten', 'avlopp', 'va', 'va-avgift', 'water']),

  // ---- Försäljning & inkomster ----------------------------------------------
  sale('forsaljning_tjanster', 'Försäljning av tjänster', 3001, 'Försäljning inom Sverige, 25 % moms', GROUPS.forsaljning, sale25, ALL,
    'Det ni fakturerar eller säljer med 25 % moms.', 'konsulttimmar, uppdrag, arvoden',
    ['försäljning', 'tjänst', 'arvode', 'faktura', 'uppdrag', 'konsult', 'intäkt', 'sales', 'invoice']),
  sale('forsaljning_varor_12', 'Försäljning 12 % moms', 3002, 'Försäljning inom Sverige, 12 % moms', GROUPS.forsaljning, sale12, ALL,
    'Livsmedel, hotellnätter och annat med 12 % moms.', 'mat, dryck utan alkohol, logi',
    ['försäljning', 'livsmedel', 'mat', 'restaurang', 'catering', 'logi', '12 %']),
  sale('forsaljning_6', 'Försäljning 6 % moms', 3003, 'Försäljning inom Sverige, 6 % moms', GROUPS.forsaljning, sale6, ALL,
    'Böcker, biljetter, persontransport och kultur med 6 % moms.', 'böcker, entrébiljetter, resor',
    ['försäljning', 'bok', 'biljett', 'entré', 'kultur', 'transport', '6 %']),
  sale('forsaljning_momsfri', 'Momsfri försäljning', 3004, 'Försäljning inom Sverige, momsfri', GROUPS.forsaljning, saleExempt, ALL,
    'Försäljning utan moms, t.ex. sjukvård, utbildning eller under omsättningsgränsen.', 'momsfria tjänster',
    ['försäljning', 'momsfri', 'utan moms', 'undantagen', 'exempt']),
  sale('hyresintakter', 'Hyresintäkter', 3910, 'Hyres- och arrendeintäkter', GROUPS.forsaljning, saleExempt, NOT_BRF,
    'Uthyrning av lokal eller utrustning. Oftast utan moms.', 'andrahandsuthyrning, uthyrd lokal',
    ['hyra', 'hyresintäkt', 'uthyrning', 'lokal', 'rent income'],
    'rr.ovriga_rorelseintakter'),
  sale('ranta', 'Ränteintäkter', 8310, 'Ränteintäkter från omsättningstillgångar', GROUPS.forsaljning, none, ALL,
    'Ränta från banken. Ingen moms.', 'sparränta, kontoränta',
    ['ränta', 'ränteintäkt', 'sparränta', 'interest'],
    'rr.ranteintakter'),

  // ---- Föreningen (inkomster) -----------------------------------------------
  sale('medlemsavgifter', 'Medlemsavgifter', 3890, 'Medlemsavgifter', GROUPS.forening, none, FORENING,
    'Årsavgifter från medlemmarna. Ingen moms.', 'medlemsavgift, familjemedlemskap',
    ['medlemsavgift', 'medlem', 'årsavgift', 'medlemskap', 'membership'],
    'rr.medlemsavgifter'),
  sale('gavor', 'Gåvor', 3993, 'Erhållna donationer och gåvor', GROUPS.forening, none, FORENING,
    'Pengar föreningen får utan motprestation.', 'gåva, donation, insamling, Swish-gåva',
    ['gåva', 'donation', 'insamling', 'swish', 'kollekt', 'gift'],
    'rr.gavor'),
  sale('bidrag_statliga', 'Statliga bidrag', 3985, 'Erhållna statliga bidrag', GROUPS.forening, none, FORENING,
    'Bidrag från staten och statliga myndigheter.', 'Riksidrottsförbundet, MUCF, Kulturrådet, LOK-stöd',
    ['bidrag', 'statligt bidrag', 'rf', 'riksidrottsförbundet', 'mucf', 'kulturrådet', 'lok-stöd', 'grant'],
    'rr.bidrag'),
  sale('bidrag_kommunala', 'Kommunala bidrag', 3987, 'Erhållna kommunala bidrag', GROUPS.forening, none, FORENING,
    'Bidrag från kommunen eller regionen.', 'föreningsbidrag, aktivitetsstöd, lokalbidrag',
    ['bidrag', 'kommun', 'kommunalt bidrag', 'region', 'föreningsbidrag', 'aktivitetsstöd', 'lokalbidrag'],
    'rr.bidrag'),
  sale('bidrag_ovriga', 'Övriga bidrag', 3989, 'Övriga erhållna offentliga bidrag', GROUPS.forening, none, FORENING,
    'Bidrag från stiftelser, förbund och andra.', 'stiftelsebidrag, sponsring utan motprestation, projektbidrag',
    ['bidrag', 'stiftelse', 'projektbidrag', 'fond', 'övrigt bidrag', 'sponsring'],
    'rr.bidrag'),

  // ---- Fastigheten (BRF, inkomster) -----------------------------------------
  sale(BRF_ARSAVGIFT_CATEGORY_ID, 'Årsavgifter', BRF_ARSAVGIFT_DEFAULT_ACCOUNT, 'Hyres- och arrendeintäkter', GROUPS.fastighet, none, BRF,
    'Medlemmarnas årsavgifter. Ingen moms.', 'månadsavgift, årsavgift',
    ['årsavgift', 'månadsavgift', 'avgift', 'medlem', 'bostadsrätt'],
    'rr.nettoomsattning'),
  sale('brf_hyresintakter', 'Hyresintäkter', 3911, 'Hyresintäkter', GROUPS.fastighet, none, BRF,
    'Hyror för lokaler, garage och förråd.', 'lokalhyra, garageplats, förråd, hyresrätt',
    ['hyra', 'hyresintäkt', 'lokal', 'garage', 'p-plats', 'förråd', 'hyresrätt'],
    'rr.nettoomsattning'),
];

export interface CatalogOptions {
  /** BRF: 3910 (default), 3020 or 3021 for årsavgifter. Ignored for other entities. */
  arsavgiftAccount?: number | undefined;
  /**
   * Enskild firma: whether the firm has employees. Without them Personalfika
   * is hidden (the owner's own fika is private). Other entities always have
   * people to serve, so the flag is ignored for them.
   */
  hasEmployees?: boolean | undefined;
}

/** Whether Personalfika is offered: every entity except an enskild firma without employees. */
export function offersPersonalfika(entity: EntityType, hasEmployees: boolean | undefined): boolean {
  switch (entity) {
    case 'enskild_firma':
      return hasEmployees === true;
    case 'aktiebolag':
    case 'ideell_forening':
    case 'bostadsrattsforening':
      return true;
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

/** Categories available to an entity type, with BRF's årsavgift account and the EF staff flag applied. */
export function catalogFor(entity: EntityType, options: CatalogOptions = {}): Category[] {
  const out: Category[] = [];
  for (const category of CATALOG) {
    if (!category.entityTypes.includes(entity)) continue;
    if (category.id === PERSONALFIKA_CATEGORY_ID && !offersPersonalfika(entity, options.hasEmployees)) continue;
    if (category.id === BRF_ARSAVGIFT_CATEGORY_ID) {
      const account = options.arsavgiftAccount;
      if (account !== undefined && BRF_ARSAVGIFT_ACCOUNTS.includes(account) && account !== category.accountNumber) {
        out.push({ ...category, accountNumber: account, formalName: account === 3910 ? 'Hyres- och arrendeintäkter' : 'Årsavgifter' });
        continue;
      }
    }
    out.push(category);
  }
  return out;
}

const byId: ReadonlyMap<string, Category> = new Map(CATALOG.map((c) => [c.id, c]));

/** Lookup in the full catalog (all entities). Prefer `findCategory(catalog, id)` with an entity catalog. */
export function categoryById(id: string): Category | undefined {
  return byId.get(id);
}
